/**
 * fontLicense/model.ts
 *
 * Shared constants and types for the font-license validation feature (MYUA-16307).
 * Backend contract lives in MYUA-16308.
 */

/**
 * File extensions treated as fonts when deciding whether a license is required.
 *
 * A superset of the backend's EXT_FONT (crhub/constants.py: ttf, otf, woff, woff2,
 * eot): legacy desktop formats (`fon`, Type 1 `pfb`/`pfm`) and font collections
 * (`ttc`, `otc`, `dfont`) are licensed the same way, so the form asks for their
 * license too. The extra ones are collected client-side only — server-side
 * enforcement (upload requirement, download gate) still follows EXT_FONT, so
 * adding an extension here is safe but does not make the backend demand it.
 *
 * `fnt` is deliberately absent: it is also the extension of generated BMFont
 * bitmap descriptors that routinely ship inside creative archives.
 */
export const FONT_EXTENSIONS = [
  'ttf',
  'otf',
  'woff',
  'woff2',
  'eot',
  'ttc',
  'otc',
  'dfont',
  'fon',
  'pfb',
  'pfm',
] as const;

/** Archive extensions we inspect on the client for fonts inside. */
export const ARCHIVE_EXTENSIONS = ['zip'] as const;

/**
 * Full Jira task URL format required by the backend
 * (crhub/settings.py JIRA_TASK_URL_PATTERN).
 */
export const JIRA_TASK_URL_PATTERN = /^https:\/\/jira\.my\.games\/browse\/AOA-\d+$/;

/**
 * The invariable part of a Jira task URL. Prefilled into the form input so the
 * user only types the task number.
 */
export const JIRA_TASK_URL_PREFIX = 'https://jira.my.games/browse/AOA-';

/**
 * Sentinel expiration date that encodes a perpetual (never-expiring) license.
 * The backend requires a concrete date, so "perpetual" is stored as this
 * far-future date; both sides recognise it as perpetual (isPerpetualExpiration).
 */
export const PERPETUAL_EXPIRATION_DATE = '2999-01-01';

/** License type as returned by GET /laws/license_types. */
export interface LicenseType {
  id: number;
  name: string;
}

/**
 * License data collected from the user for a single detected font.
 *
 * This is only the form-side value. Before the upload starts each LicenseInfo is
 * materialised into a FontLicense record via POST /laws/font_licenses (the record
 * is created without a node) and the resulting id is what travels with the upload
 * — see CreatedLicense.
 */
export interface LicenseInfo {
  /** Full Jira task URL (https://jira.my.games/browse/AOA-<digits>). */
  jiraUrl: string;
  /**
   * The font this license covers, when it is not the uploaded file itself: the
   * path of the entry inside the archive (`fonts/Arial.ttf`). `null` for a font
   * uploaded directly — there the node name already is the font.
   *
   * Sent as `filename` (FontLicense.filename, MYUA-16308) and used by the
   * moderation table to show which font of an archive a license belongs to.
   */
  filename: string | null;
  /** License type id (from GET /laws/license_types → { id, name }). */
  licenseTypeId: number;
  /**
   * Perpetual license — when true, expiresAt is ignored and the request carries
   * the PERPETUAL_EXPIRATION_DATE sentinel instead.
   */
  perpetual: boolean;
  /** Expiration date in YYYY-MM-DD, or null when perpetual. */
  expiresAt: string | null;
}

/** A font discovered either directly or inside an archive. */
export interface FontFileEntry {
  /**
   * Index of the source File within the batch array passed to detectFonts.
   * Several entries share it when one archive holds several fonts — that is how
   * bindLicenseIdsToFiles collects every license of a single uploaded file.
   */
  sourceIndex: number;
  /**
   * Stable key identifying this font within the form and the resulting
   * submission (Record<fileKey, CreatedLicense>). Built as
   * `${index}:${sourceFileName}:${archiveEntryName ?? ''}`, where `index` is the
   * position of the source File in the batch array. The leading index guarantees
   * uniqueness even when two selected files share the same name (a common
   * drag-and-drop case), so their form blocks and submission entries never mirror
   * each other. NB: the license registry is keyed by the File reference (see
   * licenseRegistry.ts), not by this string — the caller joins the two via
   * bindLicenseIdsToFiles using FontFileEntry.sourceIndex.
   */
  fileKey: string;
  /** Human-readable label shown in the form. */
  displayName: string;
  /** Name of the file selected by the user (the .ttf or the .zip). */
  sourceFileName: string;
  /** True when the font was found inside an archive. */
  insideArchive: boolean;
  /** Path of the font entry inside the archive (only when insideArchive). */
  archiveEntryName?: string;
}

/**
 * A font's license after its FontLicense record has been created
 * (POST /laws/font_licenses) but before it is attached to a node: attaching
 * happens on the backend when the upload of the file completes.
 */
export interface CreatedLicense extends LicenseInfo {
  /**
   * id of the FontLicense record. Passed to the upload endpoints as
   * `font_license_ids`. Several fonts of one uploaded file may share it when
   * their license data is identical (see LicenseConfirmationModal).
   */
  licenseId: number;
}

/** Created licenses keyed by FontFileEntry.fileKey. */
export type LicenseSubmission = Record<string, CreatedLicense>;

/**
 * Body of POST /laws/font_licenses — creates the license record up-front, with
 * no node attached. The returned id is later passed to the upload endpoints as
 * `font_license_ids`.
 */
export interface CreateFontLicensePayload {
  jira_task_url: string;
  /**
   * Optional on the backend: the font inside the archive this license covers.
   * Omitted (null) when the uploaded file is the font itself.
   */
  filename: string | null;
  /** License type id (from GET /laws/license_types). */
  license_type: number;
  /**
   * YYYY-MM-DD, strictly in the future (backend requirement). Perpetual licenses
   * carry the PERPETUAL_EXPIRATION_DATE sentinel. null should not occur in
   * practice — the form makes the date required for non-perpetual licenses.
   */
  expiration_date: string | null;
}

/**
 * Builds the stable fileKey used everywhere for a given font.
 *
 * The leading `index` is the position of the source File within the batch array
 * (the array passed to detectFonts, and kept on the entry as `sourceIndex`). It
 * disambiguates files that share the same name: two same-named files land at
 * different indices, so they get distinct keys and can carry independent licenses.
 * `archiveEntryName` further disambiguates multiple fonts packed in a single .zip
 * (same index, same source name, different entry).
 *
 * The index is stored on the entry as `sourceIndex`, so bindLicenseIdsToFiles
 * never has to rebuild the key — it groups the submission by that index instead.
 */
export function makeFontFileKey(
  index: number,
  sourceFileName: string,
  archiveEntryName?: string,
): string {
  return `${index}:${sourceFileName}:${archiveEntryName ?? ''}`;
}

/**
 * Maps collected license data to the POST /laws/font_licenses body. Perpetual
 * licenses are sent as the PERPETUAL_EXPIRATION_DATE sentinel (the backend
 * requires a concrete date and recognises this far-future date as perpetual).
 *
 * Kept here (free of network/apiScenarios deps) so the upload integration code
 * can use it without pulling the whole API layer into a dependency cycle.
 */
export function toCreateFontLicensePayload(info: LicenseInfo): CreateFontLicensePayload {
  return {
    jira_task_url: info.jiraUrl,
    filename: info.filename,
    license_type: info.licenseTypeId,
    expiration_date: toFontLicenseExpirationDate(info.perpetual, info.expiresAt),
  };
}

/** Serializes the form expiration value for both creating and updating licenses. */
export function toFontLicenseExpirationDate(
  perpetual: boolean,
  expiresAt: string | null,
): string | null {
  return perpetual ? PERPETUAL_EXPIRATION_DATE : expiresAt;
}

/** True when an expiration date is the perpetual sentinel (PERPETUAL_EXPIRATION_DATE). */
export function isPerpetualExpiration(date: string | null | undefined): boolean {
  return date === PERPETUAL_EXPIRATION_DATE;
}

/**
 * Initial form state for a stored expiration date (inverse of toFontLicenseExpirationDate).
 * The sentinel and legacy null both mean perpetual; the sentinel never lands in the date
 * field, so unchecking Perpetual leaves it empty until a real date is entered.
 */
export function toFontLicenseExpirationFormState(expirationDate: string | null | undefined): {
  perpetual: boolean;
  expiresAt: string | null;
} {
  if (expirationDate == null || isPerpetualExpiration(expirationDate)) {
    return { perpetual: true, expiresAt: null };
  }
  return { perpetual: false, expiresAt: expirationDate };
}

/**
 * Backend error codes on the upload endpoints that must NOT be retried.
 * Retrying them just wastes 5 attempts and then surfaces a generic failure
 * instead of the real, actionable cause. `invalid` is the legacy validation code
 * already handled before this feature — it also covers "Some font licenses are
 * already attached to another file", raised when a concurrent upload grabbed one
 * of our license ids first.
 */
export const NON_RETRYABLE_UPLOAD_CODES = [
  'invalid',
  'invalid_jira_url',
  'font_license_required',
] as const;

/** True when the backend upload error code should abort immediately (no retry). */
export function isNonRetryableUploadCode(code: unknown): boolean {
  return (NON_RETRYABLE_UPLOAD_CODES as readonly unknown[]).includes(code);
}

/**
 * Maps a backend upload error code to a user-facing reason. Falls back to the
 * server-provided message for unknown/legacy codes so behaviour is unchanged
 * where we don't have a nicer wording.
 */
export function uploadErrorMessage(code: unknown, fallback: string): string {
  switch (code) {
    case 'font_license_required':
      return 'This font requires a confirmed license before it can be uploaded.';
    case 'invalid_jira_url':
      return 'The font license has an invalid Jira task URL.';
    default:
      return fallback;
  }
}
